/**
 * Form validation utilities for consistent validation across the app
 */

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email format is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  // RFC 5322 compliant email regex - requires at least one dot in domain (TLD required)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate phone number format (US)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if phone format is valid
 */
export const isValidPhone = (phone) => {
  if (!phone) return true; // Phone is often optional
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === '1');
};

/**
 * Validate password strength
 * Enforces security policy requirements:
 * - Minimum 6 characters
 * - Maximum 128 characters
 * 
 * @param {string} password - Password to validate
 * @returns {{valid: boolean, errors: string[]}} Validation result with specific errors
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (!password || password.length < 6) {
    errors.push('Password must be at least 6 characters');
  }
  if (password && password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Validate required field
 * @param {any} value - Value to check
 * @param {string} fieldName - Name of field for error message
 * @returns {{valid: boolean, error?: string}}
 */
export const validateRequired = (value, fieldName = 'This field') => {
  // Check for undefined, null, empty string, AND whitespace-only strings
  const isValid = value !== undefined && value !== null && 
    (typeof value === 'string' ? value.trim() !== '' : value !== '');
  return {
    valid: isValid,
    error: isValid ? undefined : `${fieldName} is required`
  };
};

/**
 * Validate numeric value is within range
 * @param {number} value - Value to check
 * @param {number} min - Minimum value (inclusive)
 * @param {number} max - Maximum value (inclusive)
 * @param {string} fieldName - Name of field for error message
 * @returns {{valid: boolean, error?: string}}
 */
export const validateRange = (value, min, max, fieldName = 'Value') => {
  const num = Number(value);
  if (isNaN(num)) {
    return { valid: false, error: `${fieldName} must be a number` };
  }
  if (num < min || num > max) {
    return { valid: false, error: `${fieldName} must be between ${min} and ${max}` };
  }
  return { valid: true };
};

/**
 * Validate string length
 * @param {string} value - Value to check
 * @param {number} minLength - Minimum length
 * @param {number} maxLength - Maximum length
 * @param {string} fieldName - Name of field for error message
 * @returns {{valid: boolean, error?: string}}
 */
export const validateLength = (value, minLength, maxLength, fieldName = 'Field') => {
  const str = String(value || '');
  if (str.length < minLength) {
    return { valid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }
  if (str.length > maxLength) {
    return { valid: false, error: `${fieldName} must be no more than ${maxLength} characters` };
  }
  return { valid: true };
};

/**
 * Validate VIN format
 * @param {string} vin - VIN to validate
 * @returns {boolean} True if VIN format is valid
 */
export const isValidVIN = (vin) => {
  if (!vin) return false;
  // VIN must be 17 characters, no I, O, or Q
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/i;
  return vinRegex.test(vin);
};

/**
 * Validate date is in the past
 * @param {string|Date} date - Date to validate
 * @returns {boolean} True if date is in the past
 */
export const isDateInPast = (date) => {
  if (!date) return false;
  const d = new Date(date);
  return !isNaN(d.getTime()) && d < new Date();
};

/**
 * Validate date is in the future
 * @param {string|Date} date - Date to validate
 * @returns {boolean} True if date is in the future
 */
export const isDateInFuture = (date) => {
  if (!date) return false;
  const d = new Date(date);
  return !isNaN(d.getTime()) && d > new Date();
};

/**
 * Validate date range
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {{valid: boolean, error?: string}}
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return { valid: false, error: 'Both start and end dates are required' };
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { valid: false, error: 'Invalid date format' };
  }
  
  if (start > end) {
    return { valid: false, error: 'Start date must be before end date' };
  }
  
  return { valid: true };
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL format is valid
 */
export const isValidURL = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Sanitize input by trimming and removing excess whitespace
 * @param {string} value - Value to sanitize
 * @returns {string} Sanitized value
 */
export const sanitizeInput = (value) => {
  if (typeof value !== 'string') return value;
  return value.trim().replace(/\s+/g, ' ');
};

/**
 * Form validation helper - validate multiple fields at once
 * @param {Object} values - Object with field names and values
 * @param {Object} rules - Object with field names and validation rules
 * @returns {{valid: boolean, errors: Object}} Validation result
 */
export const validateForm = (values, rules) => {
  const errors = {};
  let valid = true;
  
  for (const [field, fieldRules] of Object.entries(rules)) {
    const value = values[field];
    
    for (const rule of fieldRules) {
      const result = rule(value, field);
      if (!result.valid) {
        errors[field] = result.error;
        valid = false;
        break; // Stop at first error for this field
      }
    }
  }
  
  return { valid, errors };
};

export default {
  isValidEmail,
  isValidPhone,
  validatePassword,
  validateRequired,
  validateRange,
  validateLength,
  isValidVIN,
  isDateInPast,
  isDateInFuture,
  validateDateRange,
  isValidURL,
  sanitizeInput,
  validateForm
};
